import { 
  createDraftSafeSelector,
  createEntityAdapter, 
  createSlice, 
  EntityAdapter, 
  EntityId, 
  EntityState, 
  PayloadAction, 
  Slice,
  SliceCaseReducers,
  SliceSelectors,
} from "@reduxjs/toolkit";

import { isEqual } from "lodash";
import { EntityWithId, StoreReducerFactory } from "./storeReducerFactory";
import { RootState } from ".";


export class ReducerWithSelectBySystem<ReducerName extends string, H extends string, V extends EntityWithId<H> & EntityWithId<'systemId'>>
extends StoreReducerFactory<ReducerName, H, V> {
  protected initialize(reducerName: ReducerName,idKey: H): { entityAdapter: EntityAdapter<V, EntityId>; reducerSlice: Slice<EntityState<V, EntityId>, SliceCaseReducers<EntityState<V, EntityId>>, string, string, SliceSelectors<EntityState<V, EntityId>>>; } {
    const entityAdapter = createEntityAdapter<V, EntityId>({
      selectId: (option) => option[idKey]
    });
    const { selectAll } = entityAdapter.getSelectors();
    const reducerSlice = createSlice<EntityState<V,EntityId>, any, string,SliceSelectors<EntityState<V,EntityId>>>({
      name: `${reducerName}`,
      initialState: entityAdapter.getInitialState({lastFetched: undefined}),
      reducers: {
        clear: entityAdapter.removeAll,
        upsert: (state: EntityState<V, EntityId>, { payload }: PayloadAction<V[]>) => {
          const existing = selectAll(state);
          if (existing.length !== payload.length || !isEqual(existing, payload)) {
            entityAdapter.upsertMany(state, payload);
            if ("lastFetched" in state) {
              state.lastFetched = Date.now();
            }
          }
        },
        remove: entityAdapter.removeMany,
        updateMany: entityAdapter.updateMany,
      },
      selectors: {
        selectBySystem: createDraftSafeSelector(
          entityAdapter.getSelectors().selectAll,
          (_, externalIds: EntityId[]) => externalIds,
          (entities: V[], externalIds: EntityId[]) => 
            entities.filter(entity => entity.systemId && externalIds.includes(entity.systemId))
        )
      }
    });
    return {entityAdapter: entityAdapter, reducerSlice: reducerSlice};
    
  }
  
  public get selectBySystem(): (state: RootState, systems: number[]) => V[] {
    let selectBySystem
    const selectors = this.reducerSlice.getSelectors((state: RootState) => (state.optionsState as any)[this.slice.name]) 
    
    if (typeof selectors === "object" && "selectBySystem" in selectors && selectors.selectBySystem && typeof selectors.selectBySystem === "function") {
      selectBySystem = selectors.selectBySystem;
    }
    
    if (!selectBySystem) {
      throw new Error("Select by feed curve selector not constructed correctly")
    }
    
    return selectBySystem as any;
  }

}

export function createReducerWithSelectBySystem<H extends string, T extends EntityWithId<H> & EntityWithId<'systemId'>>(reducerName: string, idKey: H): ReducerWithSelectBySystem<string, string, T> {
  return new ReducerWithSelectBySystem(reducerName, idKey);
}